import { type ReactNode } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { useAuth } from '@/auth/AuthProvider'
import { useTheme } from '@/theme/ThemeProvider'
import { Icon, type IconName } from '@/components/ui/Icon'

// Same destinations as the mobile drawer (Log · Progress · Recipes · Meal Planner ·
// Challenge · Academy · Settings) plus Measurements / Vitals, which mobile reaches
// from the Log header.
const NAV: { to: string; icon: IconName; labelKey: string; fallback: string; primary?: boolean }[] = [
  { to: '/log', icon: 'log', labelKey: 'log', fallback: 'Log', primary: true },
  { to: '/progress', icon: 'chart', labelKey: 'progress', fallback: 'Progress', primary: true },
  { to: '/recipes', icon: 'chef-hat', labelKey: 'recipes', fallback: 'Recipes', primary: true },
  { to: '/planner', icon: 'calendar', labelKey: 'navigation.mealPlanner', fallback: 'Meal Planner' },
  { to: '/challenge', icon: 'trophy', labelKey: 'navigation.challenge', fallback: '30-Day Challenge' },
  { to: '/measurements', icon: 'scale', labelKey: 'bodyMeasurements', fallback: 'Body Measurements' },
  { to: '/vitals', icon: 'heart-pulse', labelKey: 'vitals', fallback: 'Vitals' },
  { to: '/learn', icon: 'book', labelKey: 'navigation.academy', fallback: 'Academy' },
  { to: '/settings', icon: 'settings', labelKey: 'settings', fallback: 'Settings', primary: true },
]

export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, userSettings, signOut } = useAuth()
  const { colorScheme, setPreference } = useTheme()
  const streak = userSettings?.streakCount || 0

  const navItems = NAV.map((item) => (
    <NavLink
      key={item.to}
      to={item.to}
      className={({ isActive }) =>
        clsx(
          'flex items-center gap-md rounded-md px-md py-sm text-body font-medium transition-colors',
          isActive ? 'wash-primary text-primary' : 'text-text-secondary hover:bg-surface-inner hover:text-text',
        )
      }
    >
      <Icon name={item.icon} size={20} />
      <span>{t(item.labelKey, item.fallback)}</span>
    </NavLink>
  ))

  return (
    <div className="flex h-full w-full flex-col bg-bg md:flex-row">
      {/* Sidebar (desktop) */}
      <aside className="no-print hidden w-64 shrink-0 flex-col border-r border-border-light bg-surface p-md md:flex">
        <div className="mb-lg flex items-center gap-sm px-sm pt-sm">
          <img src="/icon-192.png" alt="" className="h-9 w-9 rounded-[10px]" />
          <span className="text-h4 font-bold text-text">Vore</span>
          {streak > 0 ? (
            <span className="ml-auto flex items-center gap-xs rounded-full wash-primary px-sm py-0.5 text-caption font-semibold text-primary" title={t('logComponents.streak.current', 'Current Streak')}>
              <Icon name="flame" size={14} /> {streak}
            </span>
          ) : null}
        </div>

        <button
          onClick={() => navigate('/log', { state: { openAdd: true } })}
          className="mb-md flex items-center justify-center gap-sm rounded-md bg-primary px-md py-smd text-button font-semibold text-on-primary hover:opacity-90"
        >
          <Icon name="add" size={20} /> {t('addEntry', 'Add entry')}
        </button>

        <nav className="flex flex-1 flex-col gap-xs overflow-y-auto">{navItems}</nav>

        <div className="mt-md border-t border-border-light pt-md">
          <button
            onClick={() => setPreference(colorScheme === 'dark' ? 'light' : 'dark')}
            className="mb-sm flex w-full items-center gap-md rounded-md px-md py-sm text-body-sm text-text-secondary hover:bg-surface-inner"
          >
            <Icon name={colorScheme === 'dark' ? 'sun' : 'moon'} size={18} />
            {t('darkMode', 'Dark Mode')}
          </button>
          <p className="truncate px-md text-caption text-text-muted" title={user?.email ?? ''}>{user?.email}</p>
          <button onClick={() => void signOut()} className="mt-xs flex w-full items-center gap-md rounded-md px-md py-sm text-left text-body-sm text-text-secondary hover:bg-surface-inner hover:text-red">
            <Icon name="logout" size={18} />
            {t('logout', 'Logout')}
          </button>
        </div>
      </aside>

      {/* Top bar (mobile) */}
      <header className="no-print flex items-center justify-between border-b border-border-light bg-surface px-md py-sm md:hidden">
        <span className="flex items-center gap-sm text-h4 font-bold text-text">
          <img src="/icon-192.png" alt="" className="h-7 w-7 rounded-lg" />
          Vore
        </span>
        <div className="flex items-center gap-sm">
          {streak > 0 ? (
            <span className="flex items-center gap-xs rounded-full wash-primary px-sm py-0.5 text-caption font-semibold text-primary">
              <Icon name="flame" size={14} /> {streak}
            </span>
          ) : null}
          <MobileMenu />
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-5xl px-md py-lg pb-24 md:px-xl md:pb-lg">{children}</div>
      </main>

      {/* Bottom nav (mobile) with the center "+" */}
      <nav className="no-print fixed bottom-0 left-0 right-0 z-40 flex items-end justify-around border-t border-border-light bg-surface pb-[env(safe-area-inset-bottom)] md:hidden">
        {NAV.filter((n) => n.primary).slice(0, 2).map((item) => <MobileTab key={item.to} item={item} />)}
        <button onClick={() => navigate('/log', { state: { openAdd: true } })} aria-label={t('addEntry', 'Add entry')} className="-mt-5 mb-1 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-on-primary shadow-lg">
          <Icon name="add" size={28} />
        </button>
        {NAV.filter((n) => n.primary).slice(2).map((item) => <MobileTab key={item.to} item={item} />)}
      </nav>
    </div>
  )
}

function MobileTab({ item }: { item: (typeof NAV)[number] }) {
  const { t } = useTranslation()
  return (
    <NavLink to={item.to} className={({ isActive }) => clsx('flex flex-1 flex-col items-center gap-xs py-sm text-caption', isActive ? 'text-primary' : 'text-text-muted')}>
      <Icon name={item.icon} size={22} />
      <span>{t(item.labelKey, item.fallback)}</span>
    </NavLink>
  )
}

/** Phone-width overflow menu for the secondary drawer destinations. */
function MobileMenu() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const secondary = NAV.filter((n) => !n.primary)
  return (
    <details className="relative">
      <summary className="list-none rounded-full p-xs text-text-secondary hover:bg-surface-inner [&::-webkit-details-marker]:hidden" aria-label="Menu">
        <Icon name="menu" size={22} />
      </summary>
      <div className="absolute right-0 top-10 z-50 w-56 rounded-md border border-border-light bg-surface p-xs shadow-xl">
        {secondary.map((item) => (
          <button
            key={item.to}
            onClick={(e) => {
              ;(e.currentTarget.closest('details') as HTMLDetailsElement | null)?.removeAttribute('open')
              navigate(item.to)
            }}
            className="flex w-full items-center gap-sm rounded-sm px-smd py-sm text-left text-body-sm text-text hover:bg-surface-inner"
          >
            <Icon name={item.icon} size={18} className="text-text-secondary" />
            {t(item.labelKey, item.fallback)}
          </button>
        ))}
      </div>
    </details>
  )
}
